import type { ReactNode, SVGProps } from 'react';
import { cn } from '@/lib/cn';

/* ────────────────────────────────────────────────────────────────────────────
   Icon set — hand-rolled, stroke-based, 24×24, `currentColor`.

   Every glyph is drawn on the same 24-unit grid with `strokeWidth={1.75}` and
   round caps/joins so a row of icons optically matches a row of text. Nothing
   here is filled unless the mark itself is a filled logotype (X).
   ──────────────────────────────────────────────────────────────────────────── */

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'ref'> {
  /** Rendered box in px. 18 lines up with `text-sm`; use 16 inside `text-xs`. */
  size?: number;
}

export type IconComponent = (props: IconProps) => JSX.Element;

/** Shared chrome for every icon: consistent grid, stroke and a11y defaults. */
function Glyph({
  size = 18,
  className,
  children,
  ...rest
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('shrink-0', className)}
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ── navigation / chrome ──────────────────────────────────────────────────── */

export const Search: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="M15.4 15.4 20.5 20.5" />
  </Glyph>
);

export const Plus: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </Glyph>
);

export const X: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M6 6l12 12" />
    <path d="M18 6 6 18" />
  </Glyph>
);

export const Check: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M5 12.5 9.5 17 19 7" />
  </Glyph>
);

export const Menu: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </Glyph>
);

export const Dots: IconComponent = ({ size = 18, className, ...rest }) => (
  <Glyph size={size} className={className} {...rest}>
    <circle cx="5.5" cy="12" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.15" fill="currentColor" stroke="none" />
    <circle cx="18.5" cy="12" r="1.15" fill="currentColor" stroke="none" />
  </Glyph>
);

export const ChevronDown: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M6 9l6 6 6-6" />
  </Glyph>
);

export const ChevronUp: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M6 15l6-6 6 6" />
  </Glyph>
);

export const ChevronLeft: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M15 6l-6 6 6 6" />
  </Glyph>
);

export const ChevronRight: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M9 6l6 6-6 6" />
  </Glyph>
);

export const ArrowUp: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 19.5V5" />
    <path d="M6 11l6-6 6 6" />
  </Glyph>
);

export const ArrowDown: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 4.5V19" />
    <path d="M18 13l-6 6-6-6" />
  </Glyph>
);

export const ArrowUpRight: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M7 17 17 7" />
    <path d="M9 7h8v8" />
  </Glyph>
);

export const ArrowDownRight: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M7 7l10 10" />
    <path d="M17 9v8H9" />
  </Glyph>
);

export const ExternalLink: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-8.5 8.5" />
    <path d="M18 13.5v5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6h5" />
  </Glyph>
);

export const Refresh: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
  </Glyph>
);

export const Share: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 15.5V3.75" />
    <path d="M8.25 7.5 12 3.75l3.75 3.75" />
    <path d="M7 11.5H5.5A1.5 1.5 0 0 0 4 13v6a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-6a1.5 1.5 0 0 0-1.5-1.5H17" />
  </Glyph>
);

/* ── market / data ────────────────────────────────────────────────────────── */

export const Chart: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M4 20h16" />
    <path d="M7.5 20v-5.5" />
    <path d="M12 20V9.5" />
    <path d="M16.5 20v-8" />
  </Glyph>
);

export const TrendingUp: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M3.5 17 9 11.5l3.5 3.5L21 6.5" />
    <path d="M15.5 6.5H21v5.5" />
  </Glyph>
);

export const TrendingDown: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M3.5 7 9 12.5l3.5-3.5L21 17.5" />
    <path d="M21 12v5.5h-5.5" />
  </Glyph>
);

export const Sparkles: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M11 3.25l1.55 4.2L16.75 9l-4.2 1.55L11 14.75 9.45 10.55 5.25 9l4.2-1.55L11 3.25Z" />
    <path d="M18.25 14l.65 1.85 1.85.65-1.85.65-.65 1.85-.65-1.85-1.85-.65 1.85-.65.65-1.85Z" />
    <path d="M6 16.5l.45 1.3 1.3.45-1.3.45L6 20.5l-.45-1.3-1.3-.45 1.3-.45L6 16.5Z" />
  </Glyph>
);

export const Bolt: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M13 2.75 5.75 13.5H11l-1 7.75L18.25 10.5H13V2.75Z" />
  </Glyph>
);

export const Flame: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 21.25c3.6 0 6.25-2.35 6.25-5.6 0-2.1-1-3.9-2.5-5.6-1.5-1.7-2.5-3.4-2.5-5.05 0-.62-.72-.96-1.17-.54C10 6.4 8.25 8.9 8.25 11.5c0 .9.2 1.7.6 2.4-1-.3-1.7-1.15-1.95-2.15-.65 1.15-1.15 2.5-1.15 3.9 0 3.25 2.65 5.6 6.25 5.6Z" />
  </Glyph>
);

export const Coins: IconComponent = (p) => (
  <Glyph {...p}>
    <ellipse cx="9" cy="6.75" rx="5.5" ry="2.75" />
    <path d="M3.5 6.75v3.5c0 1.52 2.46 2.75 5.5 2.75.86 0 1.68-.1 2.4-.28" />
    <ellipse cx="15" cy="15.75" rx="5.5" ry="2.75" />
    <path d="M9.5 15.75v3.5c0 1.52 2.46 2.75 5.5 2.75s5.5-1.23 5.5-2.75v-3.5" />
  </Glyph>
);

export const Layers: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 2.75 3 7.5l9 4.75 9-4.75-9-4.75Z" />
    <path d="M3 12.25 12 17l9-4.75" />
    <path d="M3 16.5 12 21.25l9-4.75" />
  </Glyph>
);

export const Sliders: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M4 6.5h9" />
    <path d="M17 6.5h3" />
    <circle cx="15" cy="6.5" r="2" />
    <path d="M4 12h3" />
    <path d="M11 12h9" />
    <circle cx="9" cy="12" r="2" />
    <path d="M4 17.5h9" />
    <path d="M17 17.5h3" />
    <circle cx="15" cy="17.5" r="2" />
  </Glyph>
);

export const Filter: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M3.75 5.5h16.5l-6.25 7.25v6.5l-4-2v-4.5L3.75 5.5Z" />
  </Glyph>
);

export const Grid: IconComponent = (p) => (
  <Glyph {...p}>
    <rect x="4" y="4" width="7" height="7" rx="1.75" />
    <rect x="13" y="4" width="7" height="7" rx="1.75" />
    <rect x="4" y="13" width="7" height="7" rx="1.75" />
    <rect x="13" y="13" width="7" height="7" rx="1.75" />
  </Glyph>
);

export const List: IconComponent = ({ size = 18, className, ...rest }) => (
  <Glyph size={size} className={className} {...rest}>
    <path d="M8.5 6.5h11.5" />
    <path d="M8.5 12h11.5" />
    <path d="M8.5 17.5h11.5" />
    <circle cx="4.5" cy="6.5" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="17.5" r="1.1" fill="currentColor" stroke="none" />
  </Glyph>
);

/* ── protocol vocabulary ──────────────────────────────────────────────────── */

export const Rocket: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 2.75c2.42 2.36 3.75 5.56 3.75 8.9v3.6L12 17.75l-3.75-2.5v-3.6c0-3.34 1.33-6.54 3.75-8.9Z" />
    <circle cx="12" cy="9.75" r="1.6" />
    <path d="M8.25 13.5 5.5 16v3.5l2.75-1.75" />
    <path d="M15.75 13.5 18.5 16v3.5l-2.75-1.75" />
    <path d="M10.75 19.5c0 1.15.47 2.12 1.25 2.65.78-.53 1.25-1.5 1.25-2.65" />
  </Glyph>
);

export const Shield: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 3l7.5 2.75v6.1c0 4.3-3 7.6-7.5 9.15-4.5-1.55-7.5-4.85-7.5-9.15v-6.1L12 3Z" />
  </Glyph>
);

export const Lock: IconComponent = (p) => (
  <Glyph {...p}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    <path d="M12 14.5v2.5" />
  </Glyph>
);

export const Anchor: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="5" r="2.75" />
    <path d="M12 21.5V7.75" />
    <path d="M8 11h8" />
    <path d="M5 12.5H2.5a9.5 9.5 0 0 0 19 0H19" />
  </Glyph>
);

export const Vault: IconComponent = (p) => (
  <Glyph {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.75" />
    <circle cx="12" cy="12" r="3.75" />
    <path d="M12 8.25V6" />
    <path d="M12 18v-2.25" />
    <path d="M8.25 12H6" />
    <path d="M18 12h-2.25" />
  </Glyph>
);

export const Trophy: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M8 4h8v4.75a4 4 0 0 1-8 0V4Z" />
    <path d="M8 5.5H5.25v1.25A3.25 3.25 0 0 0 8.5 10" />
    <path d="M16 5.5h2.75v1.25A3.25 3.25 0 0 1 15.5 10" />
    <path d="M12 12.75V17" />
    <path d="M8.5 20.25h7" />
    <path d="M9.75 20.25c0-1.25.9-2.25 2.25-2.25s2.25 1 2.25 2.25" />
  </Glyph>
);

export const Gift: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M4 10.5h16v8A1.5 1.5 0 0 1 18.5 20h-13A1.5 1.5 0 0 1 4 18.5v-8Z" />
    <path d="M3.25 7h17.5v3.5H3.25V7Z" />
    <path d="M12 7v13" />
    <path d="M12 7c0-2.2-1.3-3.5-2.6-3.5a1.75 1.75 0 0 0 0 3.5H12Z" />
    <path d="M12 7c0-2.2 1.3-3.5 2.6-3.5a1.75 1.75 0 0 1 0 3.5H12Z" />
  </Glyph>
);

export const Wallet: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M3.5 8.5A2.5 2.5 0 0 1 6 6h12a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 18 18H6a2.5 2.5 0 0 1-2.5-2.5v-7Z" />
    <path d="M20.5 10.75h-3.25a1.75 1.75 0 0 0 0 3.5h3.25" />
  </Glyph>
);

export const Users: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="9.5" cy="8" r="3.5" />
    <path d="M3.25 20v-1.25A4.25 4.25 0 0 1 7.5 14.5h4a4.25 4.25 0 0 1 4.25 4.25V20" />
    <path d="M16.5 4.75a3.5 3.5 0 0 1 0 6.5" />
    <path d="M18 14.75A4.25 4.25 0 0 1 20.75 18.75V20" />
  </Glyph>
);

export const Clock: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.25V12l3.25 2" />
  </Glyph>
);

export const Eye: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M2.5 12S6 5.75 12 5.75 21.5 12 21.5 12S18 18.25 12 18.25 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="3.25" />
  </Glyph>
);

export const Star: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M12 3.5l2.65 5.4 5.95.87-4.3 4.2 1.02 5.93L12 17.1l-5.32 2.8 1.02-5.93-4.3-4.2 5.95-.87L12 3.5Z" />
  </Glyph>
);

export const Info: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11.25V16.5" />
    <path d="M12 8h.01" />
  </Glyph>
);

export const AlertTriangle: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M10.6 4.1 3.2 17a1.6 1.6 0 0 0 1.4 2.4h14.8a1.6 1.6 0 0 0 1.4-2.4L13.4 4.1a1.6 1.6 0 0 0-2.8 0Z" />
    <path d="M12 9.25v4.25" />
    <path d="M12 16.75h.01" />
  </Glyph>
);

export const Copy: IconComponent = (p) => (
  <Glyph {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2.5" />
    <path d="M15.5 9V6.5A2.5 2.5 0 0 0 13 4H6.5A2.5 2.5 0 0 0 4 6.5V13a2.5 2.5 0 0 0 2.5 2.5H9" />
  </Glyph>
);

/* ── theme + social ───────────────────────────────────────────────────────── */

export const Sun: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.75v2" />
    <path d="M12 19.25v2" />
    <path d="M2.75 12h2" />
    <path d="M19.25 12h2" />
    <path d="M5.46 5.46 6.87 6.87" />
    <path d="M17.13 17.13l1.41 1.41" />
    <path d="M5.46 18.54 6.87 17.13" />
    <path d="M17.13 6.87l1.41-1.41" />
  </Glyph>
);

export const Moon: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M20.25 14.6A8.6 8.6 0 0 1 9.4 3.75 8.75 8.75 0 1 0 20.25 14.6Z" />
  </Glyph>
);

export const Globe: IconComponent = (p) => (
  <Glyph {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.4 2.3 3.75 5.3 3.75 8.5S14.4 18.2 12 20.5c-2.4-2.3-3.75-5.3-3.75-8.5S9.6 5.8 12 3.5Z" />
  </Glyph>
);

/** The X logotype is a filled mark — drawn as such so it reads correctly. */
export const XSocial: IconComponent = ({ size = 18, className, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    stroke="none"
    aria-hidden="true"
    focusable="false"
    className={cn('shrink-0', className)}
    {...rest}
  >
    <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.97 6.82H1.68l7.73-8.84L1.25 2.25h6.83l4.71 6.23 5.45-6.23Zm-1.16 17.52h1.83L7.08 4.13H5.12l11.96 15.64Z" />
  </svg>
);

export const Telegram: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M21 4 3.5 11.2l5.4 2 2.1 5.6 3-3.4 4.2 3.1L21 4Z" />
    <path d="M8.9 13.2 21 4" />
    <path d="M11 18.8l-.1-3.6" />
  </Glyph>
);

/* ── brand ────────────────────────────────────────────────────────────────── */

/**
 * The bull.markets mark: a rising price line above a solid, immovable floor.
 * Monochrome (inherits `currentColor`) so it works inside buttons and text.
 */
export const Bull: IconComponent = (p) => (
  <Glyph {...p}>
    <path d="M3.5 16.25 9.5 9.75l4.75 3.75L20.5 5.75" />
    <path d="M3.5 20h17" strokeWidth={2.25} />
  </Glyph>
);

/** Two-tone brand lockup: gold rise, iris floor. Ignores `currentColor`. */
export const Logo: IconComponent = ({ size = 18, className, ...rest }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    className={cn('shrink-0', className)}
    {...rest}
  >
    <path
      d="M3.5 16.25 9.5 9.75l4.75 3.75L20.5 5.75"
      className="text-bull"
      stroke="currentColor"
      strokeWidth={2}
    />
    <path d="M3.5 20h17" className="text-floor" stroke="currentColor" strokeWidth={2.5} />
  </svg>
);
